import { globalLoggerToken, growaveConfigurationToken } from "@/tokens";
import ApiClient from "@lib/ApiClient";
import { container, instanceCachingFactory, singleton } from "tsyringe";

import { ModuleInterface } from "@interfaces/ModuleInterface";

import CurrencyProviderInterface from "./CurrencyProvider/CurrencyProviderInterface";
import StaticCurrencyProvider from "./CurrencyProvider/StaticCurrencyProvider";
import {
    currencyProviderToken,
    htmlSanitizerToken,
    moneyExchangerToken,
    moneyFormatterToken,
} from "./di_tokens";
import MoneyFormatter from "./Formatters/MoneyFormatter/MoneyFormatter";
import MoneyFormatterInterface from "./Formatters/MoneyFormatter/MoneyFormatterInterface";
import GwCurrency from "./GwCurrency/GwCurrency";
import HTMLSanitizerInterface from "./HTMLSanitizer/HTMLSanitizerInterface";
import XSSHTMLSanitizer from "./HTMLSanitizer/XSSHTMLSanitizer";
import FakeMoneyExchanger from "./MoneyExchanger/FakeMoneyExchanger";
import LegacyMoneyExchanger from "./MoneyExchanger/LegacyMoneyExchanger";
import MoneyExchangerInterface from "./MoneyExchanger/MoneyExchangerInterface";

@singleton()
export class MoneyModule
    implements
        ModuleInterface<{
            moneyExchanger: MoneyExchangerInterface;
            moneyFormatter: MoneyFormatterInterface;
            currencyProvider: CurrencyProviderInterface;
            htmlSanitizer: HTMLSanitizerInterface;
            gwCurrency: GwCurrency;
        }>
{
    exports: {
        moneyExchanger: MoneyExchangerInterface;
        moneyFormatter: MoneyFormatterInterface;
        currencyProvider: CurrencyProviderInterface;
        htmlSanitizer: HTMLSanitizerInterface;
        gwCurrency: GwCurrency;
    };
    public constructor() {
        const moneyModuleLogger = container
            .resolve(globalLoggerToken)
            .newLogger("MoneyModule");

        container.register(currencyProviderToken, {
            useFactory: instanceCachingFactory(() => {
                return new StaticCurrencyProvider(
                    window.Shopify?.currency?.active || "USD",
                    moneyModuleLogger
                );
            }),
        });

        container.register(GwCurrency, {
            useFactory: instanceCachingFactory(() => {
                const gwCurrency = new GwCurrency(
                    moneyModuleLogger,
                    new ApiClient()
                );
                gwCurrency.loadRates();
                return gwCurrency;
            }),
        });
        container.register(moneyExchangerToken, {
            useFactory: instanceCachingFactory((di) => {
                if (
                    di.resolve(growaveConfigurationToken)
                        .storefrontApiAccessToken ||
                    //  use fake money exchanger if the module is initialized from the SDK,
                    //  because LegacyMoneyExchanger will make attempt to load currencies.js
                    di.resolve(growaveConfigurationToken).initedFromSdk
                ) {
                    return new FakeMoneyExchanger();
                }
                return new LegacyMoneyExchanger(
                    di.resolve(growaveConfigurationToken).shopCurrency,
                    di.resolve(GwCurrency)
                );
            }),
        });

        container.register(moneyFormatterToken, {
            useFactory: instanceCachingFactory((di) => {
                return new MoneyFormatter(
                    di.resolve(growaveConfigurationToken).moneyFormat,
                    di.resolve(
                        growaveConfigurationToken
                    ).moneyWithCurrencyFormat
                );
            }),
        });

        const HTMLSanitizer = new XSSHTMLSanitizer(moneyModuleLogger, {
            span: ["class", "id"],
            div: ["class", "id"],
            p: ["class", "id"],
            i: ["class", "id"],
            b: ["class", "id"],
            pre: ["class", "id"],
            small: ["class", "id"],
            strong: ["class", "id"],
            em: ["class", "id"],
            ul: ["class", "id"],
            ol: ["class", "id"],
            li: ["class"],
            a: ["href", "target", "class", "id"],
            svg: [
                "class",
                "id",
                "width",
                "height",
                "viewBox",
                "style",
                "xmlns",
            ],
            path: ["fill", "d"],
        });
        container.registerInstance(htmlSanitizerToken, HTMLSanitizer);

        this.exports = {
            moneyExchanger: container.resolve(moneyExchangerToken),
            moneyFormatter: container.resolve(moneyFormatterToken),
            currencyProvider: container.resolve(currencyProviderToken),
            htmlSanitizer: container.resolve(htmlSanitizerToken),
            gwCurrency: container.resolve(GwCurrency),
        };
    }
    public registerProviders() {
        return {};
    }
}
